import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Navigate, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { SITE_CONTENT } from "../content";
import {
  FaChevronRight,
  FaInstagram,
  FaTwitter,
  FaFacebookF,
} from "react-icons/fa";
import chef3 from "../assets/chef3.png";

const BlogDetailsPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  const popularPosts = SITE_CONTENT.pages.blogPage.blogs
    .filter((b) => b.slug !== slug)
    .slice(0, 2);

  useEffect(() => {
    // Find the blog with the matching slug
    const foundBlog = SITE_CONTENT.pages.blogPage.blogs.find(
      (b) => b.slug === slug,
    );
    setBlog(foundBlog);
    setLoading(false);
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#0a1a0f]"></div>;

  if (!blog) return <Navigate to="/blog" />;

  return (
    <div className="min-h-screen bg-[#0a1a0f] text-white">
      <Helmet>
        <title>{blog.metaTitle}</title>
        <meta name="description" content={blog.metaDescription} />
        <meta name="keywords" content={blog.keywords} />
        <meta property="og:title" content={blog.metaTitle} />
        <meta property="og:description" content={blog.metaDescription} />
        <meta property="og:image" content={blog.image} />
        <meta property="og:type" content="article" />
      </Helmet>

      <Header />

      <main className="pt-32 pb-20 px-6 lg:px-10 max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs tracking-wider uppercase text-white/50 mb-8">
          <Link to="/blog" className="hover:text-white transition-colors">
            Blog
          </Link>
          <FaChevronRight size={8} />
          <span className="text-white">Blog Details</span>
        </div>

        {/* Hero Image with Title Overlay */}
        <div className="relative w-full aspect-[16/9] md:aspect-[21/9] rounded-2xl overflow-hidden mb-8">
          <img
            src={blog.image}
            alt={blog.title}
            className="w-full h-full object-cover"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

          <div className="absolute bottom-0 left-0 p-6 md:p-12 w-full max-w-4xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-white mb-2 drop-shadow-lg">
              {blog.title}
            </h1>
          </div>
        </div>

        {/* Author & Meta Row */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center py-6 border-b border-white/10 mb-12 gap-6">
          <div className="flex items-center gap-4">
            <img
              src={chef3}
              alt={blog.author}
              className="w-12 h-12 rounded-full object-cover border border-white/20"
            />
            <div>
              <p className="text-sm font-medium">{blog.author}</p>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-1">
                {blog.date}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-white/10 rounded-full text-[10px] uppercase tracking-widest text-white/80">
                {blog.category}
              </span>
              <span className="text-xs text-white/50 uppercase tracking-wider">
                12 min read
              </span>
            </div>

            <div className="h-6 w-px bg-white/20 hidden md:block"></div>

            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-widest text-white/50 mr-2">
                Share this post
              </span>
              <a
                href="https://instagram.com/nawabicaterers"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Instagram"
                className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
              >
                <FaInstagram size={12} />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Twitter"
                className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
              >
                <FaTwitter size={12} />
              </a>
              <a
                href="https://www.facebook.com/sharer/sharer.php"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Facebook"
                className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
              >
                <FaFacebookF size={12} />
              </a>
            </div>
          </div>
        </div>

        {/* Main Content & Sidebar Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Main Article */}
          <article className="lg:col-span-2 bg-white text-black p-8 md:p-12 rounded-3xl shadow-xl">
            <div
              className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-black prose-h2:text-4xl prose-h2:mb-6 prose-h2:mt-12 prose-h3:text-2xl prose-h3:font-bold prose-h3:mb-4 prose-h3:mt-8 prose-p:leading-relaxed prose-p:text-gray-800 prose-p:mb-6 prose-a:text-[#c19a5b] hover:prose-a:text-[#c19a5b]/80"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <h3 className="text-2xl font-medium mb-8">Popular Post</h3>

            <div className="flex flex-col gap-8">
              {popularPosts.map((post) => (
                <div key={post.id} className="flex flex-col gap-4">
                  <Link
                    to={`/blog/${post.slug}`}
                    className="block overflow-hidden rounded-xl aspect-[16/10]"
                  >
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </Link>
                  <div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/50 mb-2">
                      <span>{post.date}</span>
                      <span className="w-1 h-1 bg-white/30 rounded-full"></span>
                      <span>By {post.author}</span>
                    </div>
                    <Link to={`/blog/${post.slug}`}>
                      <h4 className="text-lg font-medium leading-tight mb-2 hover:text-[#c19a5b] transition-colors">
                        {post.title}
                      </h4>
                    </Link>
                    <p className="text-sm text-white/60 line-clamp-2 mb-3">
                      {post.excerpt}
                    </p>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="text-xs text-[#c19a5b] uppercase tracking-widest font-medium hover:text-white transition-colors flex items-center gap-2"
                    >
                      Learn More <span className="text-[10px]">→</span>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogDetailsPage;
